import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { AiAssistantSocket } from '@mint-sdk/api-ai-assistant';
import { useSetAtom } from 'jotai';
import { WavRecorder, WavStreamPlayer } from 'wavtools';

import { VOICE_CONFIG } from '../constants/voiceChat.js';
import { waitForPlaybackEnd } from '../helpers/voiceHelper.js';
import { isS2SAtom } from './atoms/chat.js';
import { useSocket } from './SocketProvider.jsx';

const WAV_RECORDER_STATUS = {
  ENDED: 'ended',
} as const;

type VoiceChatContextValue = {
  isAvailable: boolean;
  isVoiceMode: boolean;
  isVoiceWSReady: boolean;
  isRecording: boolean;
  isUserSpeaking: boolean;
  isAiSpeaking: boolean;

  enterVoiceMode: () => Promise<void>;
  leaveVoiceMode: () => void;
  toggleRecording: () => Promise<void>;
};

const VoiceChatContext = createContext<VoiceChatContextValue | null>(null);

const DISABLED_CONTEXT: VoiceChatContextValue = {
  isAvailable: false,
  isVoiceMode: false,
  isVoiceWSReady: false,
  isRecording: false,
  isUserSpeaking: false,
  isAiSpeaking: false,
  enterVoiceMode: async () => console.warn('Voice mode is not available'),
  leaveVoiceMode: () => console.warn('Voice mode is not available'),
  toggleRecording: async () => console.warn('Voice mode is not available'),
};

type VoiceChatProviderProps = {
  children: ReactNode;
  enabled?: boolean;
  voiceModeParam?: boolean;
  onLeaveVoiceMode?: () => void;
};

export const VoiceChatProvider = ({
  children,
  enabled = true,
  voiceModeParam = false,
  onLeaveVoiceMode,
}: VoiceChatProviderProps) => {
  if (!enabled) {
    return (
      <VoiceChatContext.Provider value={DISABLED_CONTEXT}>
        {children}
      </VoiceChatContext.Provider>
    );
  }

  return (
    <VoiceChatProviderEnabled
      voiceModeParam={voiceModeParam}
      onLeaveVoiceMode={onLeaveVoiceMode}
    >
      {children}
    </VoiceChatProviderEnabled>
  );
};

type VoiceChatProviderEnabledProps = {
  children: ReactNode;
  voiceModeParam: boolean;
  onLeaveVoiceMode?: () => void;
};

const VoiceChatProviderEnabled = ({
  children,
  voiceModeParam,
  onLeaveVoiceMode,
}: VoiceChatProviderEnabledProps) => {
  const { chatSocket, chatId } = useSocket();
  const setIsS2S = useSetAtom(isS2SAtom);

  const wavRecorder = useRef(
    new WavRecorder({ sampleRate: VOICE_CONFIG.SAMPLE_RATE }),
  );
  const wavStreamPlayer = useRef<WavStreamPlayer | null>(null);
  const aiAudioSamplesBufferRef = useRef(0);
  const aiAnswerIdRef = useRef(0);
  const wsReadyResetRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  const [isVoiceMode, setIsVoiceMode] = useState(false);
  const [isVoiceWSReady, setIsVoiceWSReady] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [isAiSpeaking, setIsAiSpeaking] = useState(false);

  const enterVoiceMode = useCallback(async () => {
    if (!chatSocket) return;

    if (wavRecorder.current.getStatus() === WAV_RECORDER_STATUS.ENDED) {
      await wavRecorder.current.begin();
    }

    chatSocket.startVoiceInput('ok');
    setIsS2S(true);
    setIsVoiceMode(true);
  }, [chatSocket, setIsS2S]);

  /** The teardown itself lives in the session effect below. */
  const leaveVoiceMode = useCallback(() => {
    setIsVoiceMode(false);
    onLeaveVoiceMode?.();
  }, [onLeaveVoiceMode]);

  /** `WavRecorder` mistypes its chunk as `Int16Array`; it is a bare `ArrayBuffer`. */
  const sendVoiceChunk = useCallback(
    (chunk: AiAssistantSocket.VoiceChunk['audio']) => {
      if (!chatSocket) return;
      chatSocket.sendVoiceChunk({ audio: chunk });
    },
    [chatSocket],
  );

  const startRecording = useCallback(async () => {
    if (wavRecorder.current.getStatus() === WAV_RECORDER_STATUS.ENDED) {
      await wavRecorder.current.begin();
    }
    await wavRecorder.current.record((data) => sendVoiceChunk(data.mono));
    setIsRecording(true);
  }, [sendVoiceChunk]);

  const stopRecording = useCallback(async () => {
    setIsRecording(false);
    chatSocket?.muteVoiceInput('ok');
    setIsUserSpeaking(false);

    await wavRecorder.current.pause();
  }, [chatSocket]);

  const toggleRecording = useCallback(async () => {
    if (isRecording) {
      await stopRecording();
    } else {
      await startRecording();
    }
  }, [isRecording, stopRecording, startRecording]);

  const handleVoiceModeAvailable = useCallback(async () => {
    if (wavRecorder.current.getStatus() === WAV_RECORDER_STATUS.ENDED) {
      await wavRecorder.current.begin();
    }
    setIsVoiceWSReady(true);
  }, []);

  const handleUserSpeechStarted = useCallback(() => {
    aiAnswerIdRef.current += 1;
    wavStreamPlayer.current?.interrupt();
    setIsUserSpeaking(true);
    setIsAiSpeaking(false);
  }, []);

  const handleUserSpeechStopped = useCallback(() => {
    setIsUserSpeaking(false);
  }, []);

  const onPlaybackEnd = useCallback(
    async (answerId: number) => {
      if (!wavStreamPlayer.current) return;
      const targetSamples = aiAudioSamplesBufferRef.current;
      const getOffset = () =>
        wavStreamPlayer.current?.getTrackSampleOffset().offset;

      const isStreamActive = () => wavStreamPlayer.current?.stream;
      const isAnswerActive = () =>
        aiAnswerIdRef.current === answerId && isVoiceMode;

      await waitForPlaybackEnd({
        getOffset,
        isStreamActive,
        targetSamples,
        isAnswerActive,
      });

      if (aiAnswerIdRef.current !== answerId) return; // If user made another prompt during the answer

      setIsAiSpeaking(false);
    },
    [isVoiceMode],
  );

  const handleAgentOutputStart = useCallback(async () => {
    aiAnswerIdRef.current += 1;
    aiAudioSamplesBufferRef.current = 0;

    setIsAiSpeaking(false);

    wavStreamPlayer.current = new WavStreamPlayer({
      sampleRate: VOICE_CONFIG.SAMPLE_RATE,
    });
    await wavStreamPlayer.current.connect();

    setIsAiSpeaking(true);
  }, []);

  const handleAgentOutputChunk = useCallback(
    (data: AiAssistantSocket.AgentAudioChunk) => {
      if (isAiSpeaking && data.audio) {
        const buffer = wavStreamPlayer.current?.add16BitPCM(
          data.audio,
          'ai-answer',
        );
        if (buffer) {
          aiAudioSamplesBufferRef.current += buffer.length; // Add chunks to buffer
        }
      }
    },
    [isAiSpeaking],
  );

  const handleAgentOutputEnd = useCallback(() => {
    // Empty frame to ensure all chunks are played
    // https://mint-ai.atlassian.net/browse/PDT-80692
    const silence = new Int16Array(VOICE_CONFIG.SAMPLE_RATE);
    wavStreamPlayer.current?.add16BitPCM(silence, 'ai-answer'); // Add 1 sec of silence for audio player
    aiAudioSamplesBufferRef.current += silence.length; // Add 1 sec of silence for buffer

    onPlaybackEnd(aiAnswerIdRef.current);
  }, [onPlaybackEnd]);

  /**
   * The voice session is a resource keyed on `isVoiceMode`, so React runs this
   * cleanup exactly once per session: on leave, on a chat switch and on unmount.
   * An imperative teardown could not promise that — `WavRecorder.end()` nulls
   * its nodes only after two awaits, and an overlapping call crashes on them.
   */
  useEffect(() => {
    if (!isVoiceMode) {
      return;
    }

    // A session opened inside the previous one's leave delay would otherwise be
    // caught by that teardown's pending reset.
    clearTimeout(wsReadyResetRef.current);

    return () => {
      aiAnswerIdRef.current += 1;

      // Stop playback
      setIsAiSpeaking(false);
      wavStreamPlayer.current?.interrupt();

      // Stop recording
      if (wavRecorder.current.getStatus() !== WAV_RECORDER_STATUS.ENDED) {
        wavRecorder.current.end();
      }
      setIsRecording(false);

      chatSocket?.cancelVoiceInput();

      wsReadyResetRef.current = setTimeout(() => {
        setIsVoiceWSReady(false);
      }, VOICE_CONFIG.LEAVE_DELAY_MS);

      setIsS2S(false);
    };
  }, [isVoiceMode, chatId, chatSocket, setIsS2S]);

  /**
   * The delayed flag reset above outlives the session it was scheduled in, so
   * on unmount nothing would clear it and it would fire into a torn-down tree.
   */
  useEffect(() => () => clearTimeout(wsReadyResetRef.current), []);

  const handleVoiceModeAuth = useCallback(() => {
    if (voiceModeParam && !isVoiceMode) {
      enterVoiceMode();
    }
  }, [voiceModeParam, isVoiceMode, enterVoiceMode]);

  useEffect(() => {
    if (!chatSocket) return;

    return chatSocket.onVoiceSessionUpdated(handleVoiceModeAvailable);
  }, [chatSocket, handleVoiceModeAvailable]);

  useEffect(() => {
    if (!chatSocket) return;

    return chatSocket.onUserSpeechStarted(handleUserSpeechStarted);
  }, [chatSocket, handleUserSpeechStarted]);

  useEffect(() => {
    if (!chatSocket) return;

    return chatSocket.onUserSpeechStopped(handleUserSpeechStopped);
  }, [chatSocket, handleUserSpeechStopped]);

  useEffect(() => {
    if (!chatSocket) return;

    return chatSocket.onVoiceResponseCreated(handleAgentOutputStart);
  }, [chatSocket, handleAgentOutputStart]);

  useEffect(() => {
    if (!chatSocket) return;

    return chatSocket.onAgentAudioChunk(handleAgentOutputChunk);
  }, [chatSocket, handleAgentOutputChunk]);

  useEffect(() => {
    if (!chatSocket) return;

    return chatSocket.onAgentResponseDone(handleAgentOutputEnd);
  }, [chatSocket, handleAgentOutputEnd]);

  useEffect(() => {
    if (!chatSocket) return;

    return chatSocket.onAuthenticated(handleVoiceModeAuth);
  }, [chatSocket, handleVoiceModeAuth]);

  const value = useMemo(
    () => ({
      isAvailable: true,
      isVoiceMode,
      isVoiceWSReady,
      isRecording,
      isUserSpeaking,
      isAiSpeaking,
      enterVoiceMode,
      leaveVoiceMode,
      toggleRecording,
    }),
    [
      isVoiceMode,
      isVoiceWSReady,
      isRecording,
      isUserSpeaking,
      isAiSpeaking,
      enterVoiceMode,
      leaveVoiceMode,
      toggleRecording,
    ],
  );

  return (
    <VoiceChatContext.Provider value={value}>
      {children}
    </VoiceChatContext.Provider>
  );
};

export const useVoiceChat = (): VoiceChatContextValue => {
  const context = useContext(VoiceChatContext);

  if (!context) {
    throw new Error('useVoiceChat must be used within VoiceChatProvider');
  }

  return context;
};
